/**
 * @architecture-header 1
 * @layer module
 * @domain classrooms
 * @responsibility Render the class screen with its students, activities and review tabs.
 * @state-owner navigation store owns the active tab; dataset store owns roster and runs
 * @allowed-imports React; dataset and workspace stores; classroom store; shared components; sibling tab views
 * @forbidden direct API client; student-only surfaces
 * @public-api workspace main column
 * @side-effects navigation, dialogs and context menus
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import { CommandMenuRows } from '../../../shared/components/ContextMenu';
import { FilterBar } from '../../../shared/components/FilterBar';
import { useOverlay } from '../../../shared/components/OverlayHost';
import { Page, PageBody, PageHeader } from '../../../shared/components/PageShell';
import { QrIcon } from '../../../shared/components/VispoIcon';
import { ToolbarIconButton, VSegment } from '../../../shared/components/VTable';
import { filterDefinitions } from '../../../shared/lib/filters';
import { classMaterials, classRuns, myClassIndexes } from '../../../shared/lib/selectors';
import { useDatasetStore } from '../../../shared/model/dataset-store';
import { useTranslator } from '../../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../../shared/model/workspace-store';
import { AssignmentDialog } from '../../activities';
import { CurriculumTab } from '../../curriculum';
import { useMaterialSelection } from '../store/material-selection';
import { ClassActivitiesTab } from './ClassActivitiesTab';
import { ClassReviewTab } from './ClassReviewTab';
import { ClassroomAccessGrantDialog } from './ClassroomAccessGrantDialog';
import { ClassStudentsTab } from './ClassStudentsTab';
import { NextLessonTab } from './NextLessonTab';
import { QrInviteDialog } from './QrInviteDialog';

export function ClassDetail({ classIndex }: { classIndex: number }) {
  const t = useTranslator();
  const { openModal, openPopover } = useOverlay();
  const classes = useDatasetStore((state) => state.classes);
  const activities = useDatasetStore((state) => state.activities);
  const runs = useDatasetStore((state) => state.runs);
  const seats = useDatasetStore((state) => state.seats);
  const classTab = useWorkspaceUiStore((state) => state.classTab);
  const setTab = useWorkspaceUiStore((state) => state.setTab);
  /** Ticked materials in the 活動 tab; the header sends all of them in one pass. */
  const picked = useMaterialSelection((state) => state.picked);

  const classroom = classes[classIndex];
  const live = classRuns(runs, classIndex).filter((run) => run.status === 'live');
  const needs = live.length
    ? (seats[classIndex] ?? []).filter((seat) => seat.status === 'need' || seat.status === 'stuck')
        .length
    : 0;
  const pending = classRuns(runs, classIndex).filter(
    (run) => run.status === 'ended' && !run.sent,
  ).length;
  const activeTab = ['stu', 'act', 'cur', 'rev', 'ins'].includes(classTab) ? classTab : 'stu';

  const openDispatchMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    const ready = classMaterials(activities, classIndex).filter((item) => item.status === 'ready');
    openPopover({
      x: Math.max(14, rect.right - 340),
      y: rect.bottom + 8,
      className: 'cmenu',
      content: (
        <CommandMenuRows
          empty={
            <div style={{ padding: 12, fontSize: 14, lineHeight: '22px', color: 'var(--outline)' }}>
              {t('此班級尚無教材。請至活動頁選取後加入班級。')}
            </div>
          }
          heading={t('選擇要發放的教材')}
          items={ready.map((activity) => ({
            icon: t('題'),
            tone: 'primary' as const,
            title: activity.name,
            description: `${activity.questionCount} ${t('題')}`,
            onSelect: () =>
              openModal(<AssignmentDialog activityId={activity.id} classIndex={classIndex} />),
          }))}
        />
      ),
    });
  };

  return (
    <div className="class-detail">
      <Page>
        <PageHeader
          actions={
            <>
              <ToolbarIconButton
                title={t('班級加入連結')}
                onClick={() => openModal(<QrInviteDialog id={classIndex} kind="class" />)}
              >
                <QrIcon />
              </ToolbarIconButton>
              <button
                className="bt"
                type="button"
                onClick={() => openModal(<ClassroomAccessGrantDialog classroomId={classroom.id} />)}
              >
                {t('管理邀請')}
              </button>
              <button
                className="bt pri"
                type="button"
                onClick={(event) =>
                  picked.length
                    ? openModal(
                        <AssignmentDialog
                          activityId={picked[0]}
                          activityIds={picked}
                          classIndex={classIndex}
                        />,
                      )
                    : openDispatchMenu(event)
                }
              >
                {picked.length ? `${t('發放')}（${picked.length}）` : t('發放')}
              </button>
            </>
          }
          meta={
            <>
              {`${classroom.subtitle.split(' · ')[0]} · ${classroom.students.length} ${t('人')}`}
              {needs ? <em className="hot">{` · ${needs} ${t('人需要你')}`}</em> : null}
            </>
          }
          title={classroom.name}
        />
        <div className="vtbar class-detail-toolbar">
          {needs ? (
            <span className="class-detail-mobile-attention">{`${needs} ${t('人需要你')}`}</span>
          ) : null}
          <VSegment
            onChange={(value) => setTab('classTab', value)}
            options={[
              { value: 'stu', label: t('成員') },
              { value: 'act', label: t('活動') },
              { value: 'cur', label: t('課綱') },
              {
                value: 'rev',
                label: (
                  <>
                    {t('回顧')}
                    {pending ? <span className="vtb2">{pending}</span> : null}
                  </>
                ),
              },
              { value: 'ins', label: t('下一堂課') },
            ]}
            value={activeTab}
          />
          <div aria-hidden="true" className="class-detail-toolbar__spacer" />
          <FilterBar
            definitions={filterDefinitions('room', classes, myClassIndexes(classes))}
            scope="room"
          />
        </div>
        <PageBody>
          {activeTab === 'act' ? (
            <ClassActivitiesTab classIndex={classIndex} />
          ) : activeTab === 'cur' ? (
            <CurriculumTab classroomId={classroom.id} />
          ) : activeTab === 'rev' ? (
            <ClassReviewTab classIndex={classIndex} />
          ) : activeTab === 'ins' ? (
            <NextLessonTab classIndex={classIndex} />
          ) : (
            <ClassStudentsTab classIndex={classIndex} />
          )}
        </PageBody>
      </Page>
    </div>
  );
}
